import { Injectable } from '@angular/core';
import { Gtp } from '../../gtp';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Ruta, RutaOpcion } from 'src/app/models/ruta.model';

declare var google: any;

@Injectable({
  providedIn: 'root'
})
export class RutasService {

  // ==================== 🎯 CONFIGURACIÓN DE OPTIMIZACIÓN ====================
  // ⚙️ ACTIVAR/DESACTIVAR FUNCIONES PARA REDUCIR COSTOS DE API

  /**
   * 💰 RUTAS ALTERNATIVAS
   * true = Solicita 2-3 opciones de ruta (COSTO: 3x más llamadas a API)
   * false = Solo la mejor ruta (AHORRO: ~$30 USD/mes)
   *
   * 📊 Impacto en Costos:
   * - Activado: Cada ruta = 2-3 llamadas a Directions API ($5/1000)
   * - Desactivado: Cada ruta = 1 llamada
   */
  private ACTIVAR_RUTAS_ALTERNATIVAS: boolean = false; // 🔴 DESACTIVADO para ahorrar costos

  /**
   * 💰 OPTIMIZACIÓN DE WAYPOINTS
   * true = Google optimiza el orden de puntos intermedios (COSTO: procesamiento adicional)
   * false = Usa el orden definido por el usuario (AHORRO: procesamiento estándar)
   *
   * 📊 Recomendación: Solo activar si hay más de 4 waypoints
   */
  private ACTIVAR_OPTIMIZACION_WAYPOINTS: boolean = true; // ⚠️ Solo se usa si hay waypoints

  /**
   * 💾 SISTEMA DE CACHÉ DE RUTAS
   * Evita llamadas duplicadas a la API para las mismas rutas
   *
   * 📊 Impacto: Reduce hasta 50% las llamadas (AHORRO: ~$45 USD/mes)
   */
  private ACTIVAR_CACHE_RUTAS: boolean = true; // ✅ ACTIVADO para máximo ahorro

  /**
   * ⏱️ DURACIÓN DEL CACHÉ (milisegundos)
   * Tiempo que se considera válida una ruta en caché
   *
   * 7 días = 7 * 24 * 60 * 60 * 1000
   */
  private CACHE_DURACION_MS: number = 7 * 24 * 60 * 60 * 1000; // 7 días

  /**
   * 🔢 LÍMITE MÁXIMO DE WAYPOINTS
   * Evita llamadas muy costosas con demasiados puntos intermedios
   */
  private MAX_WAYPOINTS: number = 10;

  // ==================== FIN CONFIGURACIÓN ====================

  public rutas: Ruta[] = [];
  private rutasSubject = new BehaviorSubject<Ruta[]>([]);
  public rutas$ = this.rutasSubject.asObservable();

  // Subject para ruta temporal (vista previa)
  private rutaTemporalSubject = new BehaviorSubject<Ruta | null>(null);
  public rutaTemporal$ = this.rutaTemporalSubject.asObservable();

  private directionsService: any;

  // 💾 Sistema de caché de rutas
  private cacheRutas: Map<string, {
    opciones: RutaOpcion[],
    timestamp: number,
    origen: string,
    destino: string
  }> = new Map();

  constructor(
    private gtp: Gtp,
    private http: HttpClient,
  ) {
    // NO cargar rutas automáticamente, solo cuando se abra el panel de rutas
    // this.cargarRutasDesdeServidor();

    // Inicializar el servicio de direcciones de Google Maps
    if (typeof google !== 'undefined' && google.maps) {
      this.directionsService = new google.maps.DirectionsService();
    }
  }

  /**
   * Obtiene el observable de rutas
   */
  getRutasObs(): Observable<Ruta[]> {
    return this.rutas$;
  }

  /**
   * Actualiza las rutas y notifica a los observadores
   */
  setRutas(rutas: Ruta[]): void {
    this.rutas = rutas;
    this.rutasSubject.next(rutas);
    this.saveRutasToStorage();
  }

  /**
   * Agrega una nueva ruta
   */
  addRuta(ruta: Ruta): void {
    this.rutas.push(ruta);
    this.rutasSubject.next(this.rutas);
    this.saveRutasToStorage();
  }

  /**
   * Elimina una ruta por ID
   */
  deleteRuta(id: string): void {
    this.rutas = this.rutas.filter(r => r.id !== id);
    this.rutasSubject.next(this.rutas);
    this.saveRutasToStorage();
  }

  /**
   * Actualiza una ruta existente
   */
  updateRuta(ruta: Ruta): void {
    const index = this.rutas.findIndex(r => r.id === ruta.id);
    if (index !== -1) {
      this.rutas[index] = ruta;
      this.rutasSubject.next(this.rutas);
      this.saveRutasToStorage();
    }
  }

  /**
   * Calcula las rutas entre dos puntos usando Google Maps Directions API
   * 💰 Implementa sistema de caché para reducir costos
   */
  calcularRutas(origen: any, destino: any, waypoints?: any[]): Promise<RutaOpcion[]> {
    return new Promise((resolve, reject) => {
      if (!this.directionsService) {
        reject('Google Maps no está disponible');
        return;
      }

      // 💾 VERIFICAR CACHÉ PRIMERO (si está activado)
      if (this.ACTIVAR_CACHE_RUTAS) {
        const cacheKey = this.generarCacheKey(origen, destino, waypoints);
        const cached = this.cacheRutas.get(cacheKey);

        if (cached && (Date.now() - cached.timestamp < this.CACHE_DURACION_MS)) {
          console.log('✅ 💾 Ruta servida desde CACHÉ (ahorro de llamada a API)', {
            origen: cached.origen,
            destino: cached.destino,
            edad: Math.round((Date.now() - cached.timestamp) / (1000 * 60 * 60 * 24)) + ' días'
          });
          this.logApiUsage('directions', true); // Log con indicador de caché
          return resolve(cached.opciones);
        }
      }

      // 🔢 VALIDAR LÍMITE DE WAYPOINTS
      if (waypoints && waypoints.length > this.MAX_WAYPOINTS) {
        console.warn(`⚠️ Demasiados waypoints (${waypoints.length}). Límite: ${this.MAX_WAYPOINTS}`);
        waypoints = waypoints.slice(0, this.MAX_WAYPOINTS);
      }

      // 📍 CONSTRUIR REQUEST CON CONFIGURACIÓN OPTIMIZADA
      const request: any = {
        origin: new google.maps.LatLng(origen.latitud, origen.longitud),
        destination: new google.maps.LatLng(destino.latitud, destino.longitud),
        travelMode: google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: this.ACTIVAR_RUTAS_ALTERNATIVAS, // ⚙️ Configurable
        unitSystem: google.maps.UnitSystem.METRIC
      };

      // Agregar waypoints si existen (para múltiples puntos)
      if (waypoints && waypoints.length > 0) {
        request.waypoints = waypoints.map(wp => ({
          location: new google.maps.LatLng(wp.latitud, wp.longitud),
          stopover: true
        }));

        // Solo optimizar si hay más de 4 waypoints y está activado
        if (waypoints.length > 4 && this.ACTIVAR_OPTIMIZACION_WAYPOINTS) {
          request.optimizeWaypoints = true;
          console.log('🔄 Optimización de waypoints ACTIVADA', waypoints.length, 'puntos');
        } else {
          request.optimizeWaypoints = false;
        }
      }

      // 📡 LLAMAR A LA API
      console.log('📡 Llamando a Directions API', {
        alternativas: this.ACTIVAR_RUTAS_ALTERNATIVAS,
        waypoints: waypoints?.length || 0,
        optimizar: request.optimizeWaypoints || false
      });

      this.directionsService.route(request, (result: any, status: any) => {
        if (status === google.maps.DirectionsStatus.OK) {
          const opciones: RutaOpcion[] = [];

          result.routes.forEach((route: any, index: number) => {
            // Calcular distancia y duración totales sumando todos los legs
            let distanciaTotal = 0;
            let duracionTotal = 0;
            route.legs.forEach((leg: any) => {
              distanciaTotal += leg.distance.value;
              duracionTotal += leg.duration.value;
            });

            const puntos = route.overview_path.map((point: any) => ({
              lat: point.lat(),
              lng: point.lng()
            }));

            const opcion = new RutaOpcion({
              indice: index,
              distancia: distanciaTotal,
              duracion: duracionTotal,
              polyline: route.overview_polyline,
              puntos: puntos,
              resumen: `Vía ${route.summary || 'Ruta ' + (index + 1)}`
            });

            opciones.push(opcion);
          });

          // 💾 GUARDAR EN CACHÉ (si está activado)
          if (this.ACTIVAR_CACHE_RUTAS) {
            const cacheKey = this.generarCacheKey(origen, destino, waypoints);
            this.cacheRutas.set(cacheKey, {
              opciones: opciones,
              timestamp: Date.now(),
              origen: origen.nombre || `${origen.latitud}, ${origen.longitud}`,
              destino: destino.nombre || `${destino.latitud}, ${destino.longitud}`
            });
            console.log('💾 Ruta guardada en caché', cacheKey);
          }

          // 📊 Log de uso de API
          this.logApiUsage('directions', false);

          resolve(opciones);
        } else {
          // 🔴 Mapear errores específicos de Google Maps
          let mensajeError = 'No se pudo calcular la ruta';

          switch(status) {
            case google.maps.DirectionsStatus.NOT_FOUND:
              mensajeError = 'No se encontró una ruta entre los puntos seleccionados. Verifica que los puntos sean accesibles por carretera.';
              break;
            case google.maps.DirectionsStatus.ZERO_RESULTS:
              mensajeError = 'No hay ruta disponible entre el origen y destino. Pueden estar separados por agua, frontera cerrada o ser inaccesibles.';
              break;
            case google.maps.DirectionsStatus.MAX_WAYPOINTS_EXCEEDED:
              mensajeError = `Demasiados puntos intermedios. Máximo permitido: ${this.MAX_WAYPOINTS}`;
              break;
            case google.maps.DirectionsStatus.INVALID_REQUEST:
              mensajeError = 'Solicitud inválida. Verifica que los puntos de origen y destino sean válidos.';
              break;
            case google.maps.DirectionsStatus.OVER_QUERY_LIMIT:
              mensajeError = 'Se excedió el límite de solicitudes a Google Maps. Intenta de nuevo en unos momentos.';
              break;
            case google.maps.DirectionsStatus.REQUEST_DENIED:
              mensajeError = 'La solicitud fue rechazada por Google Maps. Verifica la configuración de la API.';
              break;
            case google.maps.DirectionsStatus.UNKNOWN_ERROR:
              mensajeError = 'Error desconocido en Google Maps. Intenta de nuevo.';
              break;
            default:
              mensajeError = `Error de Google Maps: ${status}`;
          }

          console.error('❌ Error en Directions API:', status, '-', mensajeError);
          reject(mensajeError);
        }
      });
    });
  }

  /**
   * 🔑 Genera una clave única para el caché basada en origen, destino y waypoints
   */
  private generarCacheKey(origen: any, destino: any, waypoints?: any[]): string {
    const origenKey = `${origen.gid || origen.latitud}_${origen.longitud}`;
    const destinoKey = `${destino.gid || destino.latitud}_${destino.longitud}`;
    const waypointsKey = waypoints?.map(wp => `${wp.gid || wp.latitud}_${wp.longitud}`).join('_') || 'nowp';
    return `${origenKey}_${destinoKey}_${waypointsKey}`;
  }

  /**
   * 📊 Registra el uso de la API para métricas
   */
  private logApiUsage(api: 'directions' | 'places', fromCache: boolean = false): void {
    const key = `api_${api}_${new Date().toISOString().split('T')[0]}`;
    const cacheKey = `api_${api}_cache_${new Date().toISOString().split('T')[0]}`;

    try {
      // Contar llamadas totales
      const current = parseInt(localStorage.getItem(key) || '0');
      localStorage.setItem(key, (current + 1).toString());

      // Contar hits de caché
      if (fromCache) {
        const cacheCurrent = parseInt(localStorage.getItem(cacheKey) || '0');
        localStorage.setItem(cacheKey, (cacheCurrent + 1).toString());
      }

      // Log en consola
      if (fromCache) {
        console.log(`📊 Cache Hit Rate ${api}:`,
          `${localStorage.getItem(cacheKey)}/${localStorage.getItem(key)}`,
          `(${Math.round((parseInt(localStorage.getItem(cacheKey) || '0') / parseInt(localStorage.getItem(key) || '1')) * 100)}%)`
        );
      }
    } catch (error) {
      // Silenciar errores de localStorage
    }
  }

  /**
   * 🗑️ Limpia el caché de rutas (útil para testing o cuando hay problemas)
   */
  limpiarCacheRutas(): void {
    this.cacheRutas.clear();
    console.log('🗑️ Caché de rutas limpiado');
  }

  /**
   * 📊 Obtiene estadísticas del caché
   */
  obtenerEstadisticasCache(): {
    total: number,
    masAntigua: number | null,
    maReciente: number | null
  } {
    const now = Date.now();
    let masAntigua: number | null = null;
    let maReciente: number | null = null;

    this.cacheRutas.forEach(item => {
      const edad = now - item.timestamp;
      if (masAntigua === null || edad > masAntigua) masAntigua = edad;
      if (maReciente === null || edad < maReciente) maReciente = edad;
    });

    return {
      total: this.cacheRutas.size,
      masAntigua: masAntigua ? Math.round(masAntigua / (1000 * 60 * 60 * 24)) : null, // días
      maReciente: maReciente ? Math.round(maReciente / (1000 * 60)) : null // minutos
    };
  }

  /**
   * Calcula rutas con coordenadas del mapa (sin puntos guardados)
   */
  calcularRutasConCoordenadas(puntos: { lat: number, lng: number }[]): Promise<RutaOpcion[]> {
    return new Promise((resolve, reject) => {
      if (!this.directionsService || puntos.length < 2) {
        reject('Se necesitan al menos 2 puntos');
        return;
      }

      const origen = { latitud: puntos[0].lat, longitud: puntos[0].lng };
      const destino = { latitud: puntos[puntos.length - 1].lat, longitud: puntos[puntos.length - 1].lng };

      // Los puntos intermedios son waypoints
      const waypoints = puntos.slice(1, puntos.length - 1).map(p => ({
        latitud: p.lat,
        longitud: p.lng
      }));

      this.calcularRutas(origen, destino, waypoints)
        .then(resolve)
        .catch(reject);
    });
  }

  /**
   * Guarda las rutas en localStorage
   */
  private saveRutasToStorage(): void {
    try {
      const user = JSON.parse(this.gtp.getUserData());
      const key = `rutas_${user.cod_cliente}`;
      localStorage.setItem(key, JSON.stringify(this.rutas));
    } catch (error) {
      console.error('Error al guardar rutas en localStorage:', error);
    }
  }

  /**
   * Carga las rutas desde localStorage
   */
  private loadRutasFromStorage(): void {
    try {
      const user = JSON.parse(this.gtp.getUserData());
      const key = `rutas_${user.cod_cliente}`;
      const stored = localStorage.getItem(key);

      if (stored) {
        const rutasData = JSON.parse(stored);
        this.rutas = rutasData.map((r: any) => new Ruta(r));
        this.rutasSubject.next(this.rutas);
      }
    } catch (error) {
      console.error('Error al cargar rutas desde localStorage:', error);
      this.rutas = [];
    }
  }

  /**
   * Obtiene una ruta por ID
   */
  getRutaById(id: string): Ruta | undefined {
    return this.rutas.find(r => r.id === id);
  }

  /**
   * Limpia todas las rutas
   */
  clearRutas(): void {
    this.rutas = [];
    this.rutasSubject.next(this.rutas);
    this.saveRutasToStorage();
  }

  /**
   * Establece una ruta temporal para vista previa
   */
  setRutaTemporal(ruta: Ruta | null): void {
    this.rutaTemporalSubject.next(ruta);
  }

  /**
   * Obtiene el observable de ruta temporal
   */
  getRutaTemporalObs(): Observable<Ruta | null> {
    return this.rutaTemporal$;
  }

  /**
   * Limpia la ruta temporal
   */
  clearRutaTemporal(): void {
    this.rutaTemporalSubject.next(null);
  }

  // ==================== MÉTODOS DE INTERACCIÓN CON EL SERVIDOR ====================

  /**
   * Carga las rutas desde el servidor
   */
  cargarRutasDesdeServidor(): Promise<Ruta[]> {
    return new Promise((resolve, reject) => {
      const url = `${environment.baseUrl}clientes/rutas`;

      this.http.get(url).toPromise()
        .then((response: any) => {
          console.log('📊 Rutas cargadas desde el servidor:', response);

          if (response && Array.isArray(response)) {
            // Convertir las rutas del servidor al formato del frontend
            this.rutas = response.map(rutaData => this.convertirRutaServidor(rutaData));
            this.rutasSubject.next(this.rutas);

            // También guardar en localStorage como backup
            this.saveRutasToStorage();

            resolve(this.rutas);
          } else {
            // Si no hay rutas, intentar cargar desde localStorage
            this.loadRutasFromStorage();
            resolve(this.rutas);
          }
        })
        .catch(error => {
          console.error('Error al cargar rutas del servidor:', error);
          // En caso de error, cargar desde localStorage
          this.loadRutasFromStorage();
          reject(error);
        });
    });
  }

  /**
   * Guarda una ruta en el servidor
   */
  guardarRutaEnServidor(ruta: Ruta): Promise<any> {
    return new Promise((resolve, reject) => {
      // Preparar los datos para enviar al servidor
      const datosRuta = {
        des_ruta: ruta.nombre,
        minuto_alerta: ruta.tiempoMaximoMinutos || 30,
        istrack: true,
        isrte: false,
        temporal: false,
        puntos: this.prepararPuntosParaServidor(ruta)
      };

      console.log('💾 Guardando ruta en el servidor:', datosRuta);

      const url = `${environment.baseUrl}clientes/rutas`;

      this.http.post(url, datosRuta).toPromise()
        .then((response: any) => {
          console.log('✅ Ruta guardada en el servidor:', response);

          if (response && response.success && response.ruta) {
            // Actualizar el ID de la ruta con el cod_ruta del servidor
            ruta.codRuta = response.ruta.cod_ruta;

            // Actualizar la lista local
            this.addRuta(ruta);

            resolve(response);
          } else {
            reject('Respuesta inválida del servidor');
          }
        })
        .catch(error => {
          console.error('❌ Error al guardar ruta en el servidor:', error);
          reject(error);
        });
    });
  }

  /**
   * Actualiza una ruta en el servidor
   */
  actualizarRutaEnServidor(ruta: Ruta): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!ruta.codRuta) {
        reject('La ruta no tiene cod_ruta del servidor');
        return;
      }

      const datosRuta = {
        des_ruta: ruta.nombre,
        minuto_alerta: ruta.tiempoMaximoMinutos || 30,
        istrack: true,
        isrte: false,
        temporal: false,
        puntos: this.prepararPuntosParaServidor(ruta)
      };

      console.log('🔄 Actualizando ruta en el servidor:', datosRuta);

      const url = `${environment.baseUrl}clientes/rutas/${ruta.codRuta}`;

      this.http.put(url, datosRuta).toPromise()
        .then((response: any) => {
          console.log('✅ Ruta actualizada en el servidor:', response);

          if (response && response.success) {
            // Actualizar la lista local
            this.updateRuta(ruta);

            resolve(response);
          } else {
            reject('Respuesta inválida del servidor');
          }
        })
        .catch(error => {
          console.error('❌ Error al actualizar ruta en el servidor:', error);
          reject(error);
        });
    });
  }

  /**
   * Elimina una ruta del servidor
   */
  eliminarRutaDelServidor(ruta: Ruta): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!ruta.codRuta) {
        reject('La ruta no tiene cod_ruta del servidor');
        return;
      }

      console.log('🗑️ Eliminando ruta del servidor:', ruta.codRuta);

      const url = `${environment.baseUrl}clientes/rutas/${ruta.codRuta}`;

      this.http.delete(url).toPromise()
        .then((response: any) => {
          console.log('✅ Ruta eliminada del servidor:', response);

          if (response && response.success) {
            // Eliminar de la lista local
            this.deleteRuta(ruta.id);

            resolve(response);
          } else {
            reject('Respuesta inválida del servidor');
          }
        })
        .catch(error => {
          console.error('❌ Error al eliminar ruta del servidor:', error);
          reject(error);
        });
    });
  }

  /**
   * Obtiene una ruta específica del servidor con sus detalles
   */
  obtenerRutaDelServidor(codRuta: number): Promise<any> {
    return new Promise((resolve, reject) => {
      const url = `${environment.baseUrl}clientes/rutas/${codRuta}`;

      this.http.get(url).toPromise()
        .then((response: any) => {
          console.log('📊 Ruta obtenida del servidor:', response);

          if (response) {
            const ruta = this.convertirRutaServidor(response);
            resolve(ruta);
          } else {
            reject('Ruta no encontrada');
          }
        })
        .catch(error => {
          console.error('Error al obtener ruta del servidor:', error);
          reject(error);
        });
    });
  }

  // ==================== MÉTODOS AUXILIARES ====================

  /**
   * Convierte una ruta del formato del servidor al formato del frontend
   */
  private convertirRutaServidor(rutaData: any): Ruta {
    // Extraer puntos de los detalles si existen
    let puntos: any[] = [];

    if (rutaData.detalles && Array.isArray(rutaData.detalles)) {
      puntos = rutaData.detalles
        .sort((a: any, b: any) => a.orden - b.orden)
        .map((detalle: any) => {
          // Parsear valor_punto que viene como "lat, lng"
          const coords = detalle.valor_punto.split(',').map((c: string) => parseFloat(c.trim()));
          return {
            lat: coords[0],
            lng: coords[1]
          };
        });
    }

    // Crear objeto Ruta compatible con el frontend
    const ruta = new Ruta({
      id: rutaData.cod_ruta ? rutaData.cod_ruta.toString() : Math.random().toString(),
      codRuta: rutaData.cod_ruta,
      nombre: rutaData.des_ruta,
      color: '#1976D2', // Color por defecto
      visible: false, // Por defecto ocultas, el usuario decide cuáles ver
      puntoOrigen: puntos.length > 0 ? {
        gid: rutaData.cod_ruta,
        nombre: 'Origen',
        latitud: puntos[0].lat,
        longitud: puntos[0].lng
      } : null,
      puntoDestino: puntos.length > 1 ? {
        gid: rutaData.cod_ruta,
        nombre: 'Destino',
        latitud: puntos[puntos.length - 1].lat,
        longitud: puntos[puntos.length - 1].lng
      } : null,
      puntosIntermedios: puntos.slice(1, puntos.length - 1).map((p: any, index: number) => ({
        nombre: `Punto ${index + 2}`,
        latitud: p.lat,
        longitud: p.lng
      })),
      tiempoMaximoMinutos: parseFloat(rutaData.minuto_alerta) || 30,
      polyline: puntos, // Guardar los puntos como polyline por ahora
      distanciaMetros: 0, // Se calculará después
      duracionSegundos: 0 // Se calculará después
    });

    return ruta;
  }

  /**
   * Prepara los puntos de una ruta para enviar al servidor
   * 💾 IMPORTANTE: Si hay polyline calculado, envía TODOS los puntos del trayecto
   */
  private prepararPuntosParaServidor(ruta: Ruta): any[] {
    const puntos: any[] = [];

    // 🗺️ PRIORIDAD 1: Si hay POLYLINE CALCULADO, enviar TODOS los puntos
    if (ruta.polyline && ruta.polyline.length > 0) {
      console.log('💾 Guardando polyline completo con', ruta.polyline.length, 'puntos del trayecto');

      ruta.polyline.forEach((punto: any, index: number) => {
        puntos.push({
          latitud: punto.lat,
          longitud: punto.lng,
          nombre: index === 0 ? 'Origen' :
                  index === ruta.polyline.length - 1 ? 'Destino' :
                  `Punto ${index + 1}`,
          orden: index + 1
        });
      });

      return puntos;
    }

    // 📍 FALLBACK: Si NO hay polyline, usar origen/waypoints/destino (modo antiguo)
    console.log('📍 Guardando puntos básicos (origen, waypoints, destino)');

    // Agregar punto origen
    if (ruta.puntoOrigen) {
      puntos.push({
        latitud: ruta.puntoOrigen.latitud,
        longitud: ruta.puntoOrigen.longitud,
        nombre: ruta.puntoOrigen.nombre || 'Origen',
        orden: 1
      });
    }

    // Agregar puntos intermedios
    if (ruta.puntosIntermedios && ruta.puntosIntermedios.length > 0) {
      ruta.puntosIntermedios.forEach((punto: any, index: number) => {
        puntos.push({
          latitud: punto.latitud,
          longitud: punto.longitud,
          nombre: punto.nombre || `Punto ${index + 2}`,
          orden: index + 2
        });
      });
    }

    // Agregar punto destino
    if (ruta.puntoDestino) {
      puntos.push({
        latitud: ruta.puntoDestino.latitud,
        longitud: ruta.puntoDestino.longitud,
        nombre: ruta.puntoDestino.nombre || 'Destino',
        orden: puntos.length + 1
      });
    }

    return puntos;
  }
}
